import React, { useEffect, useState, useMemo, useCallback, useContext } from 'react';
import Layout from 'components/sales/layout/layout';
import { AppliedFiltersBar, TableSectionV2 } from 'components/UI';
import localization from 'assets/localization/localization';
import ListFilters from 'components/sales/ListFilters/ListFilters';
import Images from 'assets/images/imageData';
import { BaseUrl } from 'contexts/baseUrl/baseUrl';
import { MobileActionMenu, DesktopActionMenu } from 'components/layout';
import { toast } from 'react-toastify';
const EXPORT_SUCCESS_MESSAGE =
  'Your site listing export has been requested. The CSV will be sent to your registered email shortly.';
const EXPORT_SUCCESS_SUBTEXT =
  'Large exports may take a few minutes. Please check your inbox and spam folder.';

const cell = (val) => (val != null && val !== '' ? String(val) : '—');

const formatScopeOfWork = (scope) => {
  if (scope == null || scope === '') return '—';
  return Number(scope) === 2 ? 'Park+' : 'Client';
};

const formatRupee = (val) => {
  if (val == null || val === '') return '—';
  const num = parseFloat(val);
  return Number.isNaN(num) ? cell(val) : `₹ ${num.toFixed(2)}`;
};

const formatDateDDMMYYYY = (date) => {
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}-${m}-${y}`;
};

const getDefaultCreatedRange = () => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 1);
  return {
    from_date: formatDateDDMMYYYY(start),
    to_date: formatDateDDMMYYYY(end),
  };
};

const SiteDashboard = ({
  getSiteDashboardListing,
  exportSitesCsv,
  sitesList,
  getListFilterData,
  filterDropdownList = null,
  isExportingCsv = false,
}) => {
  const { baseUrl } = useContext(BaseUrl);
  const [listData, setListData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterData, setFilterData] = useState(null);
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    if (getSiteDashboardListing !== null) {
      (async () => {
        setIsLoading(true);
        const defaultCreatedRange = getDefaultCreatedRange();
        setFilterData(defaultCreatedRange);
        await getSiteDashboardListing(defaultCreatedRange, 1);
        setIsLoading(false);
      })();
    }
  }, [getSiteDashboardListing]);

  const buildRowActions = useCallback(
    (element) => [
      {
        name: 'View Details',
        link: `${baseUrl}/site-detail/${element.site_id}`,
      },
    ],
    [baseUrl]
  );

  const tableColumns = useMemo(
    () => [
      {
        key: 'infra_project_id',
        label: 'Project Id',
        format: (_, row) => cell(row.infra_project_id ?? row.projectid),
      },
      { key: 'site_no', label: 'Site Number', format: (val) => cell(val) },
      {
        key: 'client_name',
        label: 'Business Name',
        format: (_, row) => cell(row.client_name ?? row.SiteName),
      },
      { key: 'ops_status', label: 'Site Status', format: (val) => cell(val) },
      {
        key: 'commercial_id',
        label: 'Contract Id',
        format: (_, row) => cell(row.commercial_id ?? row.contractid ?? row.comm_id),
      },
      { key: 'commercial_type', label: 'Commercial Type', format: (val) => cell(val) },
      {
        key: 'contract_length',
        label: 'Contract Period',
        format: (val) => {
          if (val == null || val === '') return '—';
          const num = Number(val);
          return Number.isNaN(num) ? cell(val) : `${num} months`;
        },
      },
      { key: 'contract_state_name', label: 'Contract State', format: (val) => cell(val) },
      {
        key: 'scope_of_work',
        label: 'Scope of Work',
        format: (_, row) => formatScopeOfWork(row.scope_of_work ?? row.Scope_of_work),
      },
      { key: 'contract_start_date', label: 'Contract Start Date', format: (val) => cell(val) },
      { key: 'contract_end_date', label: 'Contract End Date', format: (val) => cell(val) },
      { key: 'onboarding_date', label: 'Onboarding Date', format: (val) => cell(val) },
      { key: 'approved_date', label: 'Onboarded Approval Date', format: (val) => cell(val) },
      {
        key: 'live_date',
        label: 'Live Date',
        format: (_, row) => cell(row.live_date),
      },
      { key: 'current_stage', label: 'Current Stage', format: (val) => cell(val) },
      {
        key: 'contract_document',
        label: 'Agreement',
        format: (_, row) => {
          const url = row.contract_document;
          if (!url) return '—';
          return (
            <a href={url} target="_blank" rel="noopener noreferrer">
              View
            </a>
          );
        },
      },
      {
        key: 'gst_number',
        label: 'GST Number',
        format: (_, row) => cell(row.gst_details?.gst_number ?? row.gst_number),
      },
      {
        key: 'lead_no',
        label: 'Lead Number',
        format: (_, row) => cell(row.lead_number ?? row.lead_no),
      },
      { key: 'lead_type', label: 'Lead Type', format: (val) => cell(val) },
      { key: 'lead_sub_type_name', label: 'Lead Sub Type', format: (val) => cell(val) },
      { key: 'location_type', label: 'Location Type', format: (val) => cell(val) },
      { key: 'city', label: 'City', format: (val) => cell(val) },
      { key: 'zone', label: 'Zone', format: (val) => cell(val) },
      { key: 'area', label: 'Area', format: (val) => cell(val) },
      { key: 'sales_person_name', label: 'Sales Person', format: (val) => cell(val) },
      {
        key: 'assigned_to_installation',
        label: 'Assigned To (Installation)',
        format: (val) => cell(val),
      },
      {
        key: 'assigned_to_ba',
        label: 'Assigned To (Brand Activation)',
        format: (val) => cell(val),
      },
      {
        key: 'approver_name',
        label: 'Approver',
        format: (_, row) => cell(row.approver_name ?? row.Approver_name),
      },
      {
        key: 'total_mg_amount',
        label: 'Capex Amount',
        format: (_, row) => formatRupee(row.total_mg_amount ?? row.fusion_capex),
      },
      {
        key: 'installation_amount',
        label: 'Installation Amount',
        format: (_, row) => formatRupee(row.installation_amount ?? row.fusion_installation),
      },
      {
        key: 'subscription_amount',
        label: 'Rental Amount',
        format: (_, row) => formatRupee(row.subscription_amount ?? row.fusion_rental),
      },
      {
        key: 'payment_term_type',
        label: 'Rental Pay Term',
        format: (_, row) => cell(row.payment_term_type ?? row.fusion_rental_pay_term),
      },
      {
        key: 'cartage_amount',
        label: 'Cartage Amount',
        format: (_, row) => formatRupee(row.cartage_amount ?? row.fusion_cartage),
      },
      { key: 'fusion_client_partner', label: 'Channel Partner Id', format: (val) => cell(val) },
      { key: 'fusion_manual_ins', label: 'Manual Installation', format: (val) => cell(val) },
      {
        key: 'fusion_manual_capex_rental',
        label: 'Manual Capex/Rental',
        format: (val) => cell(val),
      },
      { key: 'increment_type', label: 'Increment Type', format: (val) => cell(val) },
      {
        key: 'fusion_increament_amount',
        label: 'Increment Amount',
        format: (val) => formatRupee(val),
      },
      {
        key: 'milestone_1_amount',
        label: 'Milestone 1 Amount',
        format: (_, row) => formatRupee(row.milestone_1_amount ?? row.Milestone1),
      },
      {
        key: 'milestone_1_remark',
        label: 'Milestone 1 Remark',
        format: (_, row) => cell(row.milestone_1_remark ?? row.Milestone1_Remark),
      },
      {
        key: 'milestone_2_amount',
        label: 'Milestone 2 Amount',
        format: (_, row) => formatRupee(row.milestone_2_amount ?? row.Milestone2),
      },
      {
        key: 'milestone_2_remark',
        label: 'Milestone 2 Remark',
        format: (_, row) => cell(row.milestone_2_remark ?? row.Milestone2_Remark),
      },
      {
        key: 'milestone_3_amount',
        label: 'Milestone 3 Amount',
        format: (_, row) => formatRupee(row.milestone_3_amount ?? row.Milestone3),
      },
      {
        key: 'milestone_3_remark',
        label: 'Milestone 3 Remark',
        format: (_, row) => cell(row.milestone_3_remark ?? row.Milestone3_Remark),
      },
      {
        key: 'total_amount',
        label: 'Total Amount',
        format: (_, row) => formatRupee(row.total_amount ?? row.Total),
      },
      {
        key: 'actions',
        label: 'Actions',
        getActions: (row) => buildRowActions(row),
      },
    ],
    [buildRowActions]
  );

  useEffect(() => {
    if (sitesList.resultSets !== undefined) {
      if (sitesList.resultSets.length > 0) {
        setListData(sitesList.resultSets);
        sitesList.current_page && setCurrentPage(sitesList.current_page);
        setPageCount(sitesList.last_page || sitesList.current_page || 1);
      } else {
        setListData([]);
        setPageCount(0);
      }
    }
  }, [sitesList]);

  const applyFilter = async (appliedFilterData) => {
    setIsLoading(true);
    setFilterData(appliedFilterData);
    await getSiteDashboardListing(appliedFilterData, 1);
    setIsLoading(false);
    filterToggle();
  };

  const removeFilter = async () => {
    setIsLoading(true);
    const defaultCreatedRange = getDefaultCreatedRange();
    setFilterData(defaultCreatedRange);
    await getSiteDashboardListing(defaultCreatedRange, 1);
    setIsLoading(false);
  };

  const appliedFilterLabels = useMemo(() => {
    const labels = [];
    if (filterData?.site_no) labels.push(`Site: ${filterData.site_no}`);
    if (filterData?.client_name) labels.push(`Business: ${filterData.client_name}`);
    if (filterData?.infra_project_id) labels.push(`Project: ${filterData.infra_project_id}`);
    if (filterData?.site_status) labels.push(`Site Status: ${filterData.site_status}`);
    if (filterData?.contract_period) labels.push(`Contract Period: ${filterData.contract_period} months`);
    const onboardingFrom = filterData?.onboarding_from_date;
    const onboardingTo = filterData?.onboarding_to_date;
    const onboardingFromValid =
      onboardingFrom && String(onboardingFrom).toLowerCase() !== 'invalid date';
    const onboardingToValid = onboardingTo && String(onboardingTo).toLowerCase() !== 'invalid date';
    if (onboardingFromValid || onboardingToValid) {
      labels.push(
        `Onboarding: ${onboardingFromValid ? onboardingFrom : '—'} to ${
          onboardingToValid ? onboardingTo : '—'
        }`
      );
    }
    const liveFrom = filterData?.live_from_date;
    const liveTo = filterData?.live_to_date;
    const liveFromValid = liveFrom && String(liveFrom).toLowerCase() !== 'invalid date';
    const liveToValid = liveTo && String(liveTo).toLowerCase() !== 'invalid date';
    if (liveFromValid || liveToValid) {
      labels.push(`Live: ${liveFromValid ? liveFrom : '—'} to ${liveToValid ? liveTo : '—'}`);
    }
    const from = filterData?.from_date;
    const to = filterData?.to_date;
    const fromValid = from && String(from).toLowerCase() !== 'invalid date';
    const toValid = to && String(to).toLowerCase() !== 'invalid date';
    if (fromValid || toValid) {
      labels.push(`Date: ${fromValid ? from : '—'} to ${toValid ? to : '—'}`);
    }
    return labels;
  }, [filterData]);

  useEffect(() => {
    (async () => {
      await getListFilterData(2);
    })();
  }, []);

  const filterToggle = () => {
    setShowFilter(!showFilter);
  };

  const headerBtns = () => {
    const actionsMenuItems = [
      {
        label: localization.showFilters,
        onClick: filterToggle,
        icon: Images?.Filter,
      },
      {
        label: isExportingCsv ? 'Requesting export…' : 'Export CSV',
        onClick: async () => {
          if (isExportingCsv) return;
          const result = await exportSitesCsv(filterData);
          if (result?.success) {
            toast.success(
              <div>
                <div>{EXPORT_SUCCESS_MESSAGE}</div>
                <div className="small text-muted mt-1">{EXPORT_SUCCESS_SUBTEXT}</div>
              </div>
            );
            return;
          }
          toast.error(result?.message || 'Export request failed');
        },
        icon: Images?.fileIcon,
      },
    ];

    return (
      <div className="d-flex align-items-center flex-wrap gap-2 site-dashboard-header-actions">
        <MobileActionMenu items={actionsMenuItems} toggleLabel="Actions" />
        <DesktopActionMenu items={actionsMenuItems} toggleLabel="Actions" />
      </div>
    );
  };

  const onPageChange = async (val) => {
    const filter = filterData === null ? null : filterData;
    setIsLoading(true);
    await getSiteDashboardListing(filter, val.selected + 1);
    setIsLoading(false);
  };

  const srNo =
    sitesList?.current_page && sitesList?.per_page
      ? (sitesList.current_page - 1) * sitesList.per_page + 1
      : 1;

  return (
    <Layout
      title={localization.siteDashboard}
      Buttons={headerBtns}
      isLoading={isLoading}
      isGoback
      linkFlag
      linkUrl={`${baseUrl}/dashboard`}
      useNewHeaderLayout
    >
      <AppliedFiltersBar
        items={appliedFilterLabels}
        onClear={removeFilter}
        clearButtonLabel="Reset to last 1 month"
        clearButtonDisabled={isLoading}
      />
      <TableSectionV2
        hideHeader
        hideTitleAndStatusFilter
        className="site-dashboard-table-section"
        tableData={listData}
        tableColumns={tableColumns}
        tableSectionProps={{ srNo }}
        pageCount={pageCount}
        currentPage={currentPage}
        onPageChange={onPageChange}
        isLoading={isLoading}
        emptyMessage="No sites found."
      />

      <ListFilters
        showFilter={showFilter}
        filterToggle={filterToggle}
        applyFilter={(val) => applyFilter(val)}
        siteDashboardFilter
        listFilterData={{
          ...(filterDropdownList?.data || {}),
          appliedFilterValues: filterData,
        }}
      />
    </Layout>
  );
};

export default SiteDashboard;
